import React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import countryList from 'react-select-country-list';
import { toast } from 'react-toastify';
import { Input, Label } from 'reactstrap';
import FormGroup from 'reactstrap/lib/FormGroup';
import styled from 'styled-components';
import { BillingInfoDTO, SubDomainType } from '../../api/model';
import Analytics from '../../lib/user-analytics';
import { selectMyProfile } from '../../store/Account/selectors';
import PricingHeader, { PricingStep } from './pricing-header-indicator';

interface BillingInformationProps {
    updatePaymentInfo: (paymentInfo: BillingInfoDTO) => void;
    setCountryCode: (countryCode: string) => void;
    goBackOne: () => void;
    planType: SubDomainType;
    returnToPricing: () => void;
    domainName: string;
}

const BillingInformation = (props: BillingInformationProps) => {
    const myProfile = useSelector(selectMyProfile);

    const [company, setCompany] = useState('');
    const [country, setCountry] = useState('');
    const [lastName, setLastName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [formValid, setFormValid] = useState(true);

    useEffect(() => {
        setFormValid(
            isValidInput(firstName) && isValidInput(lastName) && isValidInput(company) && isValidInput(country)
        );
    }, [firstName, lastName, country, company]);

    useEffect(() => {
        if (myProfile) {
            setFirstName(myProfile.firstName || '');
            setLastName(myProfile.lastName || '');
        }
    }, [myProfile]);

    const handleSubmit = () => {
        if (!formValid) {
            toast.error('Please check the details entered and try again.');
            return;
        }

        const payment: BillingInfoDTO = {
            firstName: firstName,
            lastName: lastName,
            company: company,
            country: country,
        };
        Analytics.Event('Plus', 'Submitted billing information');
        props.updatePaymentInfo(payment);
    };

    const isValidInput = (val: string) => (val?.length > 0 ? true : false);

    return (
        <SubdomainPurchaseContainer>
            <SubdomainPurchaseForm>
                <SubdomainPurchaseLogo>
                    <SubdomainLogo src="/assets/subdomain/soar-plus-logo.png" />
                    <SubdomainProPlan />
                </SubdomainPurchaseLogo>
                <SubdomainPurchaseInput>
                    <PricingHeader step={PricingStep.Address} planType={props.planType} />
                    <SubdomainStepsContainer>
                        <SubdomainPurchaseText>
                            <p>Please enter contact information for</p>
                            <h3 title={`${props.domainName}.soar.earth`}>{props.domainName}.soar.earth</h3>
                        </SubdomainPurchaseText>
                        <React.Fragment>
                            <SubdomainBillingDetails>
                                <FormGroup className="billing-left-col">
                                    <Label for="firstname">First Name</Label>
                                    <SubdomainNameInput
                                        className="billing-left-col"
                                        placeholder="First Name"
                                        type="text"
                                        onChange={(e) => setFirstName(e.target.value)}
                                        value={firstName}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="lastname">Last Name</Label>
                                    <SubdomainNameInput
                                        placeholder="Last Name"
                                        type="text"
                                        onChange={(e) => setLastName(e.target.value)}
                                        value={lastName}
                                    />
                                </FormGroup>
                            </SubdomainBillingDetails>
                            <SubdomainBillingDetails>
                                <FormGroup className="billing-left-col">
                                    <Label for="organisation">Organisation</Label>
                                    <SubdomainNameInput
                                        placeholder="Organisation"
                                        type="text"
                                        onChange={(e) => setCompany(e.target.value)}
                                        value={company}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label for="country">Country</Label>
                                    <LocationSelect
                                        onChange={(result) => {
                                            const selected = JSON.parse(result.target.value);
                                            setCountry(selected.label);
                                            props.setCountryCode(selected.value);
                                        }}
                                    >
                                        <option key="Country" value={`{"value":"","label":""}`}>
                                            Country
                                        </option>
                                        {countryList()
                                            .getData()
                                            .map((key) => {
                                                if (key.label === 'Antarctica') return null;
                                                return (
                                                    <option key={key.label} value={JSON.stringify(key)}>
                                                        {key.label}
                                                    </option>
                                                );
                                            })}
                                    </LocationSelect>
                                </FormGroup>
                            </SubdomainBillingDetails>
                        </React.Fragment>
                        <SubdomainPurchaseButtonContainer>
                            <SubdomainPurchaseButton className="subdomain-back" onClick={props.goBackOne}>
                                BACK
                            </SubdomainPurchaseButton>
                            <SubdomainPurchaseButton
                                className="subdomain-create"
                                disabled={!formValid}
                                type="submit"
                                onClick={handleSubmit}
                            >
                                CONTINUE
                            </SubdomainPurchaseButton>
                        </SubdomainPurchaseButtonContainer>
                    </SubdomainStepsContainer>
                </SubdomainPurchaseInput>
            </SubdomainPurchaseForm>
        </SubdomainPurchaseContainer>
    );
};

export default BillingInformation;

const SubdomainPurchaseContainer = styled.div`
    width: 100vw;
    height: 100vh;
    display: flex;
`;

const SubdomainPurchaseForm = styled.div`
    width: 550px;
    margin: auto;
    background: rgba(0, 0, 0, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;

    @media only screen and (max-width: 600px) {
        flex-direction: column;
        width: 100%;
        margin: 50px 5px auto 5px;
    }
`;

const SubdomainPurchaseLogo = styled.div`
    display: flex;
    flex-direction: row;
`;

const SubdomainLogo = styled.img`
    width: 425px;

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`;

const SubdomainProPlan = styled.div`
    background-image: url("data:image/svg+xml,%3Csvg width='106' height='64' viewBox='0 0 106 64' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.4833 0.875975C5.58276 0.875975 3.40912 0.915284 0.128906 1.22974V28.3514H6.13605V18.2889C7.71688 18.4854 9.49531 18.6034 11.2737 18.6034C18.0318 18.6034 20.4425 16.7166 20.4425 9.72001C20.4425 2.80201 18.0318 0.875975 10.4833 0.875975ZM10.3648 14.0045H6.13605V5.47488H10.3648C13.4474 5.47488 14.3959 6.33963 14.3959 9.72001C14.3959 13.1004 13.4474 13.9651 10.3648 14.0045Z' fill='white'/%3E%3Cpath d='M47.4849 19.9005C46.8525 17.1097 44.9951 16.127 43.6118 15.8126V15.616C47.4849 15.1444 48.8286 12.7073 48.8286 8.89457C48.8286 2.68409 45.8645 0.875975 38.7508 0.875975C33.8502 0.875975 31.6371 0.915284 28.3569 1.22974V28.3514H34.285V17.7779H38.5137C40.134 17.7779 40.9244 18.3282 41.4382 20.0577L43.4933 28.3514H49.8561L47.4849 19.9005ZM38.6322 13.1397H34.285V5.51418C35.5891 5.47488 37.0119 5.47488 38.6322 5.47488C41.7939 5.47488 42.7424 6.26101 42.7424 9.28764C42.7424 12.275 41.7939 13.1397 38.6322 13.1397Z' fill='white'/%3E%3Cpath d='M68.3765 0.71875C59.761 0.71875 56.7179 3.9026 56.7179 14.7906C56.7179 25.6786 59.761 28.8624 68.3765 28.8624C76.992 28.8624 80.0746 25.6786 80.0746 14.7906C80.0746 3.9026 76.992 0.71875 68.3765 0.71875ZM68.3765 5.55349C72.4866 5.55349 73.7118 7.59745 73.7118 14.7906C73.7118 21.9837 72.4866 24.0277 68.3765 24.0277C64.2663 24.0277 63.0412 21.9837 63.0412 14.7906C63.0412 7.59745 64.2663 5.55349 68.3765 5.55349Z' fill='white'/%3E%3Cpath d='M10.4833 36.3801C5.58276 36.3801 3.40912 36.4194 0.128906 36.7338V63.8555H6.13605V53.793C7.71688 53.9895 9.49531 54.1074 11.2737 54.1074C18.0318 54.1074 20.4425 52.2207 20.4425 45.2241C20.4425 38.3061 18.0318 36.3801 10.4833 36.3801ZM10.3648 49.5085H6.13605V40.979H10.3648C13.4474 40.979 14.3959 41.8437 14.3959 45.2241C14.3959 48.6045 13.4474 49.4692 10.3648 49.5085Z' fill='white'/%3E%3Cpath d='M34.285 36.7338H28.2778V58.1954C28.2778 61.5364 30.1353 63.9735 34.5221 63.9735C38.3951 63.9735 42.4262 63.9342 46.0621 63.5411L45.7855 58.9815H36.4191C34.8778 58.9815 34.285 58.3919 34.285 56.9768V36.7338Z' fill='white'/%3E%3Cpath d='M70.0996 63.8555H76.2254L68.3607 38.3061C68.1236 37.3234 67.2937 36.7338 66.2661 36.7338H60.5752C59.5871 36.7338 58.7177 37.3234 58.4806 38.3061L50.616 63.8555H56.7812L58.7572 56.741H68.1236L70.0996 63.8555ZM60.0219 52.1028L62.3141 43.8091L62.9069 41.2541H63.9739C64.0925 42.0796 64.3296 42.9836 64.5272 43.8091L66.8194 52.1028H60.0219Z' fill='white'/%3E%3Cpath d='M106 36.7338H100.309V52.3386C100.309 53.8716 100.427 55.3653 100.546 56.8982H100.151C99.5185 55.3653 98.7676 53.7144 98.1748 52.5352L91.2586 38.5026C90.6658 37.3234 90.1125 36.7338 88.8479 36.7338H85.1725C83.7497 36.7338 82.9988 37.4807 82.9988 38.8957V63.8555H88.7293V47.7397C88.7293 46.3247 88.6503 44.831 88.4527 43.416H88.8874C89.2036 44.7524 89.994 46.4426 90.5473 47.6218L97.7005 62.0867C98.2933 63.2659 98.8466 63.8555 100.111 63.8555H103.866C105.288 63.8555 106 63.1087 106 61.6937V36.7338Z' fill='white'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    width: 106px;
    height: 64px;
    margin-top: 27px;
`;

const SubdomainPurchaseInput = styled.div``;

const SubdomainPurchaseText = styled.div`
    margin: 25px 0px;

    h3 {
        color: #eed926 !important;
        font-size: 24px;
        text-align: left;
        padding-left: 3px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    p {
        color: rgba(255, 255, 255, 0.87);
        font-size: 18px;
        padding-left: 3px;
        padding-top: 5px;
        margin-bottom: 2px;
    }

    a {
        color: #eed926 !important;
        font-size: 18px;
    }

    @media only screen and (max-width: 600px) {
        margin: 10px 0px 10px 0px;

        h3 {
            font-size: 18px;
        }

        p,
        a {
            font-size: 14px;
        }
    }
`;

const SubdomainStepsContainer = styled.div`
    margin: 13px;
`;

const SubdomainNameInput = styled(Input)`
    width: 100%;
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.6);
    color: rgba(255, 255, 255, 0.6);

    :focus {
        background-color: transparent;
        color: rgba(255, 255, 255, 0.6);
        border-color: #eed926;
        outline: 0;
        box-shadow: 0 0 0 0.1rem rgba(238, 218, 38, 0.25);
    }
`;

const SubdomainBillingDetails = styled.div`
    display: flex;
    flex-direction: row;

    .form-group {
        width: 100%;

        &.billing-left-col {
            margin-right: 10px;
        }
    }

    label {
        color: rgba(255, 255, 255, 0.87);
    }

    @media only screen and (max-width: 600px) {
        flex-direction: column;

        .form-group {
            margin-bottom: 0px;

            &.billing-left-col {
                margin-right: 0px;
            }
        }
    }
`;

const LocationSelect = styled.select`
    width: 100%;
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.87);
    display: block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.6);
    background-clip: padding-box;
    border-radius: 0.25rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    option {
        color: rgba(255, 255, 255, 0.6);
        background-color: rgba(0, 0, 0, 0.8);
    }

    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 6px;
        background-color: #343a40;
    }

    &::-webkit-scrollbar-thumb {
        box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        background-color: #eed926;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
        box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        background-color: #343a40;
        margin-top: 2px;
        margin-bottom: 0;
    }

    :focus,
    :active {
        background-color: transparent;
        border: 1px solid rgba(238, 217, 38, 0.6);
        box-shadow: 0 0 0 0.1rem rgba(238, 217, 38, 0.3);
        color: rgba(255, 255, 255, 0.6);
    }
`;

const SubdomainPurchaseButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    padding-top: 20px;

    @media only screen and (max-width: 600px) {
        flex-direction: column;
        margin: 10px 0px;
    }
`;

const SubdomainPurchaseButton = styled.button`
    border-radius: 6px;
    font-size: 18px;
    padding: 5px 57px;
    cursor: pointer;

    &.subdomain-back {
        background-color: transparent;
        color: white;
        margin-right: 10px;
        border: 1px solid white;
    }

    &.subdomain-create {
        background-color: #eed926;
        color: black;
    }
    background-color: gray;
    width: -webkit-fill-available;

    &.subdomain-create:disabled {
        cursor: default;
        background-color: grey;
    }

    @media only screen and (max-width: 600px) {
        padding: 5px 0px;
        font-size: 14px;

        &.subdomain-back {
            margin-right: 0px;
            margin-bottom: 5px;
        }
    }

    :not(disabled):hover {
        opacity: 0.9;
    }
`;
